import React, { useEffect } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";

import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseTextField from "../../../components/ui/BaseTextField";

import { showToast } from "../../../store/toast/toastSlice";
import {
  fetchQualificationTypes,
  createSubQualification,
} from "../../../services/api/qualification";

const validationSchema = yup.object({
  qualificationTypeId: yup.number().required("Qualification is required"),
  name: yup.string().trim().required("Sub-qualification is required"),
});

export default function AddEditSubQualificationModal({
  open,
  close,
  mode = "add",
  initialData = null,
  refetchSubQualifications,
}) {
  const isEdit = mode === "edit";
  const dispatch = useDispatch();

  const { data: qualTypesRes } = useQuery({
    queryKey: ["qualificationTypes"],
    queryFn: () => fetchQualificationTypes({}),
  });

  const qualificationOptions =
    qualTypesRes?.data?.data?.map((q) => ({ label: q.name, value: q.id })) ||
    qualTypesRes?.data?.map((q) => ({ label: q.name, value: q.id })) ||
    [];

  const { mutateAsync: createMutation, isPending: isSaving } = useMutation({
    mutationFn: createSubQualification,
  });

  const handleSubmit = async () => {
    const { qualificationTypeId, name } = formik.values;
    try {
      await createMutation({
        qualificationTypeId,
        subQualifications: [name.trim()],
      });
      dispatch(
        showToast({
          message: isEdit ? "Saved" : "Sub-qualification created",
          type: "success",
        })
      );
      refetchSubQualifications?.();
      formik.resetForm();
      close(false);
    } catch (e) {
      dispatch(showToast({ message: "Failed to save", type: "error" }));
    }
  };

  const formik = useFormik({
    initialValues: { qualificationTypeId: null, name: "" },
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    if (!open) return;
    formik.setValues(
      {
        qualificationTypeId: initialData?.qualificationTypeId ?? null,
        name: initialData?.name ?? "",
      },
      false
    );
  }, [open, initialData]);

  return (
    <form>
      <BaseModal
        className="-top-[200px] overflow-auto"
        open={open}
        close={close}
      >
        <div className="py-7 px-12 xl:w-[1000px] lg:w-[900px] sm:w-[700px]">
          <h6 className="text-dark font-normal border-b pb-1">
            {isEdit ? "Edit Sub-Qualification" : "Create New Sub-Qualification"}
          </h6>

          <div className="grid grid-cols-2 gap-x-8 mt-4">
            <BaseAutoSelect
              className="mb-3"
              placeholder="Select qualification"
              fullHeight={false}
              required
              labelShrink={false}
              label={<span className="font-semibold">Qualification Type</span>}
              options={qualificationOptions}
              id="qualificationTypeId"
              name="qualificationTypeId"
              isDefaultField={false}
              defaultValue={formik.values.qualificationTypeId}
              value={formik.values.qualificationTypeId}
              onChange={(e) =>
                formik.setFieldValue(
                  "qualificationTypeId",
                  typeof e?.target?.value === "string"
                    ? Number(e.target.value)
                    : e?.target?.value
                )
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.qualificationTypeId &&
                Boolean(formik.errors.qualificationTypeId)
              }
              helperText={
                formik.touched.qualificationTypeId &&
                formik.errors.qualificationTypeId
              }
            />

            <div>
              <p className="font-semibold mb-2">Sub-Qualification Name</p>
              <BaseTextField
                id="name"
                name="name"
                placeholder="Enter sub-qualification"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] bg-light text-primary !hover:text-primary"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            className="px-[12px] py-[5px]"
            color="primary"
            onClick={formik.handleSubmit}
            disabled={
              !formik.values.qualificationTypeId ||
              !formik.values.name.trim() ||
              isSaving
            }
          >
            {isEdit ? "Save" : "Create"}
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
}
